import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Maximize2, Minimize2, ExternalLink, Crosshair, RotateCcw, Upload, Eye, EyeOff, Trash2, Edit3, Save, X, Download } from 'lucide-react';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap, Circle, Marker, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { cellToBoundary } from 'h3-js';
import JSZip from 'jszip';
import * as turf from '@turf/turf';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// Service Area center (calculated from KML coordinates)
const SERVICE_AREA_CENTER = [13.075, 80.21];
const DEFAULT_OPACITY = 0.7;
const SERVICE_AREA_ZOOM = 13;

// Helper to recenter map
const RecenterAutomatically = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng]);
  }, [lat, lng, map]);
  return null;
};

// Map Controls Panel (right side)
const MapControlsPanel = ({ center, zoom, opacity, onOpacityChange, kmzOverlay, onKmzUpload, onKmzToggle, onKmzRemove, showWeirdArea, onToggleWeirdArea, onImportWeirdAreaKmz, isEditMode, onToggleEditMode, onExportKmz, weirdAreaDemandSum }) => {
  const map = useMap();
  const fileInputRef = useRef(null);
  const weirdAreaFileInputRef = useRef(null);
  const controlsRef = useRef(null);

  // Prevent map click propagation for controls
  useEffect(() => {
    if (controlsRef.current) {
      L.DomEvent.disableClickPropagation(controlsRef.current);
      L.DomEvent.disableScrollPropagation(controlsRef.current);
    }
  }, []);

  const handleRecentre = () => {
    map.setView(center, zoom, { animate: true });
  };

  const resetOpacity = () => {
    onOpacityChange(DEFAULT_OPACITY);
  };

  // Handle Weird Area KMZ import
  const handleWeirdAreaKmzChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      // Find KML file in KMZ
      let kmlContent = null;
      for (const filename of Object.keys(zip.files)) {
        if (filename.endsWith('.kml')) {
          kmlContent = await zip.files[filename].async('string');
          break;
        }
      }

      if (!kmlContent) {
        alert('No KML file found in KMZ');
        return;
      }

      // Parse KML to extract coordinates
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      // Find all coordinate elements
      const coordinateElements = kmlDoc.getElementsByTagName('coordinates');
      const polygons = [];

      for (let i = 0; i < coordinateElements.length; i++) {
        const coordText = coordinateElements[i].textContent.trim();
        const coords = coordText.split(/\s+/).map(coord => {
          const [lng, lat] = coord.split(',').map(Number);
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            return [lat, lng]; // Convert to Leaflet format [lat, lng]
          }
          return null;
        }).filter(c => c !== null);

        if (coords.length >= 3) {
          polygons.push(coords);
        }
      }

      if (polygons.length > 0) {
        // Call the import handler with parsed coordinates
        if (polygons.length === 1) {
          onImportWeirdAreaKmz({ type: 'single', coordinates: polygons[0] });
        } else {
          onImportWeirdAreaKmz({ type: 'multi', coordinates: polygons });
        }
        // Show the weird area after import
        if (!showWeirdArea) {
          onToggleWeirdArea();
        }
      } else {
        alert('No valid polygon coordinates found in KMZ file');
      }
    } catch (error) {
      console.error('Error parsing KMZ:', error);
      alert('Error parsing KMZ file. Please ensure it\'s a valid KMZ/KML file.');
    }

    // Reset file input
    e.target.value = '';
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      // Find KML file in the KMZ
      let kmlContent = null;
      for (const filename of Object.keys(zip.files)) {
        if (filename.endsWith('.kml')) {
          kmlContent = await zip.files[filename].async('string');
          break;
        }
      }

      if (!kmlContent) {
        alert('No KML file found in KMZ');
        return;
      }

      // Parse KML to extract polygon coordinates
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      // Extract all polygons
      const polygons = [];
      const placemarks = kmlDoc.getElementsByTagName('Placemark');

      for (let i = 0; i < placemarks.length; i++) {
        const placemark = placemarks[i];
        const nameEl = placemark.getElementsByTagName('name')[0];
        const name = nameEl ? nameEl.textContent : `Polygon ${i + 1}`;

        const coordsElements = placemark.getElementsByTagName('coordinates');
        for (let j = 0; j < coordsElements.length; j++) {
          const coordsText = coordsElements[j].textContent.trim();
          const coords = coordsText.split(/\s+/).map(coord => {
            const [lng, lat] = coord.split(',').map(Number);
            return [lat, lng]; // Leaflet uses [lat, lng]
          }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));

          if (coords.length > 2) {
            polygons.push({ name, coordinates: coords });
          }
        }
      }

      // Also extract points
      const points = [];
      const pointElements = kmlDoc.getElementsByTagName('Point');
      for (let i = 0; i < pointElements.length; i++) {
        const coordsEl = pointElements[i].getElementsByTagName('coordinates')[0];
        if (coordsEl) {
          const [lng, lat] = coordsEl.textContent.trim().split(',').map(Number);
          if (!isNaN(lat) && !isNaN(lng)) {
            const parentPlacemark = pointElements[i].closest('Placemark');
            const nameEl = parentPlacemark?.getElementsByTagName('name')[0];
            points.push({
              name: nameEl ? nameEl.textContent : `Point ${i + 1}`,
              position: [lat, lng]
            });
          }
        }
      }

      if (polygons.length === 0 && points.length === 0) {
        alert('No valid polygons or points found in KMZ file');
        return;
      }

      onKmzUpload({
        filename: file.name,
        polygons,
        points,
        visible: true
      });

    } catch (error) {
      console.error('Error parsing KMZ:', error);
      alert('Error parsing KMZ file: ' + error.message);
    }

    // Reset file input
    e.target.value = '';
  };

  return (
    <div
      ref={controlsRef}
      className="absolute top-4 right-4 z-[5000] flex flex-col gap-2 pointer-events-auto max-h-[calc(100%-2rem)] overflow-y-auto"
      style={{ maxHeight: 'calc(100% - 2rem)' }}
    >
      {/* Recentre Button */}
      <button
        onClick={handleRecentre}
        className="bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 text-sm font-medium text-slate-700 transition-all hover:shadow-xl"
        title="Recentre to Service Area"
      >
        <Crosshair className="w-4 h-4 text-indigo-600" />
        Recentre
      </button>

      {/* Rework Service Area */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Service Area Editor
        </div>

        {!isEditMode ? (
          <button
            onClick={onToggleEditMode}
            className="w-full px-3 py-2 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-lg text-xs font-medium text-amber-700 transition-colors flex items-center justify-center gap-1.5"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Rework Service Area
          </button>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-amber-600 font-medium text-center py-1 bg-amber-50 rounded">
              ✏️ Drawing Mode Active
            </div>
            <div className="text-[10px] text-slate-500 text-center">
              Draw polygons to add to area
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onToggleEditMode(false)}
                className="flex-1 px-2 py-1.5 bg-green-100 hover:bg-green-200 rounded text-xs font-medium text-green-700 transition-colors flex items-center justify-center gap-1"
              >
                <Save className="w-3 h-3" />
                Save
              </button>
              <button
                onClick={() => onToggleEditMode(true)}
                className="px-2 py-1.5 bg-red-100 hover:bg-red-200 rounded text-xs font-medium text-red-700 transition-colors"
                title="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Export KMZ Button - only show when weird area is visible */}
        {showWeirdArea && !isEditMode && (
          <button
            onClick={onExportKmz}
            className="w-full mt-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-xs font-medium text-slate-700 transition-colors flex items-center justify-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            Export as KMZ
          </button>
        )}
      </div>

      {/* KMZ Overlay Upload */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          KMZ Overlay
        </div>

        {kmzOverlay ? (
          <div className="space-y-2">
            <div className="text-xs text-slate-600 truncate max-w-[150px]" title={kmzOverlay.filename}>
              {kmzOverlay.filename}
            </div>
            <div className="flex gap-1">
              <button
                onClick={onKmzToggle}
                className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${kmzOverlay.visible
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                title={kmzOverlay.visible ? 'Hide overlay' : 'Show overlay'}
              >
                {kmzOverlay.visible ? <Eye className="w-3.5 h-3.5 mx-auto" /> : <EyeOff className="w-3.5 h-3.5 mx-auto" />}
              </button>
              <button
                onClick={onKmzRemove}
                className="px-2 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                title="Remove overlay"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".kmz,.kml"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-3 py-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg text-xs font-medium text-orange-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" />
              Upload KMZ
            </button>
          </>
        )}
      </div>

      {/* Weird Area Toggle */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Weird Area
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={onToggleWeirdArea}
            className={`w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${showWeirdArea
              ? 'bg-gray-800 text-white hover:bg-gray-900'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-300'
              }`}
          >
            {showWeirdArea ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {showWeirdArea ? 'Hide Weird Area' : 'Show Weird Area'}
          </button>

          {/* Import KMZ for Weird Area */}
          <input
            ref={weirdAreaFileInputRef}
            type="file"
            accept=".kmz,.kml"
            onChange={handleWeirdAreaKmzChange}
            className="hidden"
          />
          <button
            onClick={() => weirdAreaFileInputRef.current?.click()}
            className="w-full px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-xs font-medium text-slate-600 transition-colors flex items-center justify-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" />
            Import KMZ
          </button>

          {/* Demand Sum Display - only show when weird area is visible */}
          {showWeirdArea && weirdAreaDemandSum && weirdAreaDemandSum.total > 0 && (
            <div className="bg-gray-900 text-white rounded-lg p-2.5 mt-1">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                Total Demand Inside
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-emerald-400">
                  {weirdAreaDemandSum.total.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400">
                  ({weirdAreaDemandSum.hexCount} hexes)
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transparency Slider */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Hex Opacity
          </label>
          <button
            onClick={resetOpacity}
            className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 transition-colors"
            title="Reset to default (70%)"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={opacity * 100}
          onChange={(e) => onOpacityChange(parseInt(e.target.value) / 100)}
          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>0%</span>
          <span className="font-bold text-indigo-600">{Math.round(opacity * 100)}%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
};

// Create a custom DivIcon for demand labels
const createDemandLabel = (demand, color) => {
  // Determine text color based on background brightness
  // Light backgrounds: #38BDF8, #2DD4BF, '#86EFAC', #FDE047', #FB923C
  const lightBgColors = ['#38BDF8', '#2DD4BF', '#86EFAC', '#FDE047', '#FB923C'];
  const isLightBg = lightBgColors.includes(color);
  const textColor = isLightBg ? '#1a1a1a' : '#ffffff';

  return L.divIcon({
    className: 'demand-label',
    html: `<div style="
      font-size: 11px;
      font-weight: 700;
      color: ${textColor};
      text-shadow: ${isLightBg ? 'none' : '0 1px 2px rgba(0,0,0,0.5)'};
      text-align: center;
      pointer-events: none;
    ">${demand}</div>`,
    iconSize: [30, 20],
    iconAnchor: [15, 10]
  });
};

// SLA color scale
const getSlaColor = (score) => {
  const safeScore = Math.max(0, Math.min(100, score));
  const hue = (safeScore / 100) * 120;
  return `hsl(${hue}, 70%, 45%)`;
};

export const MapVisualizer = ({ clusters, metric, locationType = 'PICKUP', height = '100%', showServiceArea, onToggleServiceArea, hexOpacity = 0.7, onOpacityChange, kmzOverlay, onKmzUpload, onKmzToggle, onKmzRemove, showWeirdArea, onToggleWeirdArea, weirdAreaCoords, onUpdateWeirdArea }) => {
  const [isClient, setIsClient] = useState(false);
  const [selectedHexes, setSelectedHexes] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [drawnLayers, setDrawnLayers] = useState([]);
  const featureGroupRef = useRef(null);

  // Service Area polygon coordinates from KML (lat, lng format for Leaflet) - "Fish Bowl"
  const serviceAreaCoordinates = [
    [13.0607455, 80.1604917],
    [13.0635885, 80.1675297],
    [13.0645915, 80.1714777],
    [13.067046, 80.176235],
    [13.065167, 80.17771],
    [13.064756, 80.179072],
    [13.0627924, 80.1802961],
    [13.060211, 80.181901],
    [13.0594632, 80.1832028],
    [13.05859, 80.184408],
    [13.054517, 80.1867691],
    [13.0510675, 80.1880929],
    [13.0477898, 80.1893604],
    [13.0462802, 80.1898457],
    [13.0409328, 80.1907344],
    [13.0385044, 80.1921629],
    [13.0370829, 80.1932784],
    [13.0364555, 80.1973125],
    [13.0359953, 80.2007458],
    [13.0354937, 80.2050802],
    [13.0350756, 80.2078483],
    [13.0349083, 80.2106163],
    [13.0344066, 80.214736],
    [13.0340094, 80.2178257],
    [13.0338212, 80.2208726],
    [13.0336332, 80.2247136],
    [13.0341141, 80.2270097],
    [13.0343442, 80.228512],
    [13.0349086, 80.2313016],
    [13.0360561, 80.2338506],
    [13.0362116, 80.2370135],
    [13.037956, 80.239833],
    [13.04026, 80.2430603],
    [13.042204, 80.2455064],
    [13.0444824, 80.24821],
    [13.0485376, 80.2515574],
    [13.053889, 80.2567933],
    [13.0558539, 80.2587887],
    [13.0574843, 80.2599259],
    [13.0602434, 80.2615137],
    [13.0631701, 80.2624581],
    [13.0665985, 80.2628016],
    [13.068396, 80.2627371],
    [13.0696083, 80.2626725],
    [13.0712805, 80.2624576],
    [13.0731302, 80.2620071],
    [13.0745618, 80.2615565],
    [13.0761711, 80.2610845],
    [13.0777386, 80.2606125],
    [13.0801003, 80.2596684],
    [13.0824585, 80.2580955],
    [13.0839302, 80.2568799],
    [13.0853183, 80.2555355],
    [13.0885823, 80.2539327],
    [13.0926027, 80.2515025],
    [13.0975315, 80.2470086],
    [13.0980325, 80.2444336],
    [13.1000375, 80.2380406],
    [13.101718, 80.2326381],
    [13.104451, 80.2283351],
    [13.105603, 80.2255781],
    [13.106841, 80.2206681],
    [13.10665, 80.2159531],
    [13.107941, 80.2101171],
    [13.106835, 80.1980861],
    [13.105622, 80.1938202],
    [13.101454, 80.1880991],
    [13.1005405, 80.1846957],
    [13.0988685, 80.1766277],
    [13.0993705, 80.1683877],
    [13.1015435, 80.1618647],
    [13.0880005, 80.1613497],
    [13.0607455, 80.1604917]
  ];

  // Weird Area polygon coordinates (from weird.kmz) - use prop if provided, otherwise default
  const defaultWeirdAreaCoordinates = [
    [13.1015893, 80.1607487],
    [13.0599548, 80.1602337],
    [13.0673783, 80.1770722],
    [13.0307484, 80.1925271],
    [13.032529, 80.2548191],
    [13.0831765, 80.2771136],
    [13.0921219, 80.2764055],
    [13.1041969, 80.2225403],
    [13.1015893, 80.1607487]
  ];

  // Handle both old format (array) and new format ({ type, coordinates })
  const parseWeirdAreaCoords = (coords) => {
    if (!coords) {
      return { type: 'single', polygons: [defaultWeirdAreaCoordinates] };
    }
    // New format with type
    if (coords.type === 'multi') {
      return { type: 'multi', polygons: coords.coordinates };
    }
    if (coords.type === 'single') {
      return { type: 'single', polygons: [coords.coordinates] };
    }
    // Old format - plain array
    if (Array.isArray(coords) && coords.length > 0) {
      return { type: 'single', polygons: [coords] };
    }
    return { type: 'single', polygons: [defaultWeirdAreaCoordinates] };
  };

  const weirdAreaData = parseWeirdAreaCoords(weirdAreaCoords);
  // For backwards compatibility, keep a single array reference for merging
  const weirdAreaCoordinates = weirdAreaData.polygons[0];

  // Handle draw events
  const handleCreated = (e) => {
    const { layer } = e;
    const coords = layer.getLatLngs();

    // Flatten nested array if needed (for polygons)
    let flatCoords = coords;
    if (coords[0] && Array.isArray(coords[0]) && coords[0][0] && typeof coords[0][0].lat === 'number') {
      flatCoords = coords[0];
    } else if (coords[0] && typeof coords[0].lat === 'number') {
      flatCoords = coords;
    }

    const newCoords = flatCoords.map(c => [c.lat, c.lng]);
    setDrawnLayers(prev => [...prev, newCoords]);
  };

  const handleDeleted = (e) => {
    // Clear all drawn layers when deleted
    setDrawnLayers([]);
  };

  // Toggle edit mode and handle save
  const handleToggleEditMode = (cancel = false) => {
    if (isEditMode && !cancel) {
      // Save mode - merge drawn layers with existing weird area using Turf.js union
      if (drawnLayers.length > 0 && onUpdateWeirdArea) {
        try {
          const mergedCoords = mergePolygonsWithTurf(weirdAreaCoordinates, drawnLayers);
          onUpdateWeirdArea(mergedCoords);
        } catch (error) {
          console.error('Error merging polygons:', error);
          alert('Error merging polygons. Please try drawing again.');
        }
      }
      setDrawnLayers([]);
      // Clear drawn features from the map
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }
    } else if (cancel) {
      // Cancel - discard changes
      setDrawnLayers([]);
      // Clear drawn features from the map
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }
    }
    setIsEditMode(!isEditMode);
  };

  // Merge polygons using Turf.js union
  const mergePolygonsWithTurf = (original, additions) => {
    // Convert Leaflet coordinates [lat, lng] to GeoJSON [lng, lat]
    const toGeoJSONCoords = (coords) => {
      const geoCoords = coords.map(([lat, lng]) => [lng, lat]);
      // Ensure polygon is closed
      if (geoCoords.length > 0 &&
        (geoCoords[0][0] !== geoCoords[geoCoords.length - 1][0] ||
          geoCoords[0][1] !== geoCoords[geoCoords.length - 1][1])) {
        geoCoords.push([...geoCoords[0]]);
      }
      return geoCoords;
    };

    // Convert GeoJSON coordinates back to Leaflet [lat, lng]
    const toLeafletCoords = (geoCoords) => {
      return geoCoords.map(([lng, lat]) => [lat, lng]);
    };

    // Create Turf polygon from original weird area
    let mergedPolygon = turf.polygon([toGeoJSONCoords(original)]);

    // Union each drawn polygon with the result
    for (const drawnCoords of additions) {
      try {
        const drawnPolygon = turf.polygon([toGeoJSONCoords(drawnCoords)]);
        mergedPolygon = turf.union(turf.featureCollection([mergedPolygon, drawnPolygon]));
      } catch (error) {
        console.error('Error unioning polygon:', error);
        // If union fails, try to continue with next polygon
        continue;
      }
    }

    // Extract coordinates from merged polygon
    if (mergedPolygon && mergedPolygon.geometry) {
      const coords = mergedPolygon.geometry.coordinates;
      // Handle both Polygon and MultiPolygon results
      if (mergedPolygon.geometry.type === 'Polygon') {
        return { type: 'single', coordinates: toLeafletCoords(coords[0]) };
      } else if (mergedPolygon.geometry.type === 'MultiPolygon') {
        // For MultiPolygon (non-overlapping shapes), preserve ALL exact shapes
        // Return array of polygons to maintain exact outlines
        const allPolygons = coords.map(poly => toLeafletCoords(poly[0]));
        return { type: 'multi', coordinates: allPolygons };
      }
    }

    // Fallback to original if merge fails
    return { type: 'single', coordinates: original };
  };

  // Export KMZ function - handles multiple polygons
  const handleExportKmz = async () => {
    const kmlContent = generateKML(weirdAreaData.polygons, 'Reworked Service Area');

    const zip = new JSZip();
    zip.file('doc.kml', kmlContent);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'reworked_service_area.kmz';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Generate KML content - supports multiple polygons
  const generateKML = (polygonsArray, name) => {
    const placemarks = polygonsArray.map((coordinates, idx) => {
      const coordString = coordinates
        .map(([lat, lng]) => `${lng},${lat},0`)
        .join('\n              ');

      return `    <Placemark>
      <name>${name}${polygonsArray.length > 1 ? ` Part ${idx + 1}` : ''}</name>
      <styleUrl>#serviceAreaStyle</styleUrl>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              ${coordString}
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>`;
    }).join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${name}</name>
    <Style id="serviceAreaStyle">
      <LineStyle>
        <color>ff000000</color>
        <width>5</width>
      </LineStyle>
      <PolyStyle>
        <color>33000000</color>
      </PolyStyle>
    </Style>
${placemarks}
  </Document>
</kml>`;
  };

  const toggleSelection = (hexId) => {
    setSelectedHexes(prev => {
      const next = new Set(prev);
      if (next.has(hexId)) {
        next.delete(hexId);
      } else {
        next.add(hexId);
      }
      return next;
    });
  };

  const handleOpenNewTab = () => {
    // Save current state to localStorage - REMOVED to prevent QuotaExceededError
    // Large datasets cannot be reliably stored in localStorage
    /*
    const mapData = {
      clusters,
      metric,
      locationType,
      center: SERVICE_AREA_CENTER
    };
    try {
        localStorage.setItem('rideflow_map_data', JSON.stringify(mapData));
        window.open('/map', '_blank');
    } catch (e) {
        console.error('Failed to save map data to localStorage:', e);
        alert('Cannot open new tab: Data too large for browser storage.');
    }
    */
    alert("Opening in new tab is temporarily disabled for large datasets.");
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Default to service area center
  const chennaiCenter = SERVICE_AREA_CENTER;

  const chennaiBounds = new L.LatLngBounds(
    [12.75, 79.90],
    [13.40, 80.50]
  );

  // Process clusters & compute center & percentiles
  const { center, validClusters, maxDemand, percentiles } = useMemo(() => {
    if (!clusters || clusters.length === 0) {
      return {
        center: chennaiCenter,
        validClusters: [],
        maxDemand: 0,
        percentiles: { p10: 0, p20: 0, p30: 0, p40: 0, p50: 0, p60: 0, p70: 0, p80: 0, p90: 0 }
      };
    }

    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    let maxD = 0;
    const valid = [];
    const demands = [];

    clusters.forEach(c => {
      if (c.lat === 0 && c.lng === 0) return;

      valid.push(c);
      demands.push(c.demand);
      minLat = Math.min(minLat, c.lat);
      maxLat = Math.max(maxLat, c.lat);
      minLng = Math.min(minLng, c.lng);
      maxLng = Math.max(maxLng, c.lng);
      maxD = Math.max(maxD, c.demand);
    });

    if (valid.length === 0) {
      return {
        center: chennaiCenter,
        validClusters: [],
        maxDemand: 0,
        percentiles: { p10: 0, p20: 0, p30: 0, p40: 0, p50: 0, p60: 0, p70: 0, p80: 0, p90: 0 }
      };
    }

    // Calculate Percentiles for demand distribution (10 levels)
    demands.sort((a, b) => a - b);
    const getP = (p) => demands[Math.floor(demands.length * p)] || 0;

    return {
      center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2],
      validClusters: valid,
      maxDemand: maxD,
      percentiles: {
        p10: getP(0.1),
        p20: getP(0.2),
        p30: getP(0.3),
        p40: getP(0.4),
        p50: getP(0.5),
        p60: getP(0.6),
        p70: getP(0.7),
        p80: getP(0.8),
        p90: getP(0.9)
      }
    };
  }, [clusters]);

  // Calculate sum of demands inside Weird Area (including partially overlapping hexes)
  const weirdAreaDemandSum = useMemo(() => {
    if (!showWeirdArea || !validClusters.length || !weirdAreaData.polygons.length) {
      return { total: 0, hexCount: 0 };
    }

    try {
      // Convert all weird area polygons to Turf polygons
      const weirdAreaPolygons = weirdAreaData.polygons.map(coords => {
        const geoCoords = coords.map(([lat, lng]) => [lng, lat]);
        // Ensure polygon is closed
        if (geoCoords.length > 0 &&
          (geoCoords[0][0] !== geoCoords[geoCoords.length - 1][0] ||
            geoCoords[0][1] !== geoCoords[geoCoords.length - 1][1])) {
          geoCoords.push([...geoCoords[0]]);
        }
        return turf.polygon([geoCoords]);
      });

      let totalDemand = 0;
      let hexCount = 0;

      validClusters.forEach(cluster => {
        try {
          // Get hex boundary
          const boundary = cellToBoundary(cluster.hexId);
          const hexCoords = boundary.map(([lat, lng]) => [lng, lat]);
          // Close the hex polygon
          hexCoords.push([...hexCoords[0]]);
          const hexPolygon = turf.polygon([hexCoords]);

          // Check if hex intersects with any weird area polygon
          for (const weirdPoly of weirdAreaPolygons) {
            // Use booleanIntersects to check if hex touches or overlaps the weird area
            if (turf.booleanIntersects(hexPolygon, weirdPoly)) {
              totalDemand += cluster.demand;
              hexCount++;
              break; // Only count each hex once
            }
          }
        } catch (e) {
          // Skip invalid hexes
        }
      });

      return { total: totalDemand, hexCount };
    } catch (e) {
      console.error('Error calculating weird area demand:', e);
      return { total: 0, hexCount: 0 };
    }
  }, [showWeirdArea, validClusters, weirdAreaData]);

  // Dynamic color scale based on demand percentiles - 10 quantiles
  // Deep Blue → Blue → Light Blue → Teal → Green → Yellow → Orange → Dark Orange → Red → Deep Red
  const getCustomDemandColor = (value) => {
    if (value === 0 || maxDemand === 0) return 'transparent';

    const { p10, p20, p30, p40, p50, p60, p70, p80, p90 } = percentiles;

    // 10 quantile colors from cool to hot
    const colors = [
      '#1E3A8A',  // 0-10%:   Deep Blue
      '#2563EB',  // 10-20%:  Blue
      '#38BDF8',  // 20-30%:  Light Blue
      '#2DD4BF',  // 30-40%:  Teal
      '#86EFAC',  // 40-50%:  Green
      '#FDE047',  // 50-60%:  Yellow
      '#FB923C',  // 60-70%:  Orange
      '#F97316',  // 70-80%:  Dark Orange
      '#EF4444',  // 80-90%:  Red
      '#991B1B'   // 90-100%: Deep Red
    ];

    // Determine which quantile bucket the value falls into
    if (value <= p10) return colors[0];
    if (value <= p20) return colors[1];
    if (value <= p30) return colors[2];
    if (value <= p40) return colors[3];
    if (value <= p50) return colors[4];
    if (value <= p60) return colors[5];
    if (value <= p70) return colors[6];
    if (value <= p80) return colors[7];
    if (value <= p90) return colors[8];
    return colors[9];
  };

  // Build polygons
  const polygons = useMemo(() => {
    if (!validClusters.length) return [];

    return validClusters
      .map(cluster => {
        try {
          const boundary = cellToBoundary(cluster.hexId);

          const color =
            metric === 'SLA'
              ? getSlaColor(cluster.slaComplianceScore)
              : getCustomDemandColor(cluster.demand);

          return {
            ...cluster,
            positions: boundary,
            color
          };
        } catch {
          console.error("Invalid hex", cluster.hexId);
          return null;
        }
      })
      .filter(Boolean);
  }, [validClusters, metric, maxDemand]);

  // Stable hook
  const mapInstanceKey = useMemo(() => {
    if (!validClusters.length) return `${metric}-empty`;
    return `${metric}-${validClusters.map(c => c.hexId).join('|')}`;
  }, [metric, validClusters]);

  // FINAL JSX — no early returns anywhere
  return (
    <div className={`relative w-full rounded-xl overflow-hidden border border-slate-200 shadow-sm z-0`} style={{ height }}>
      {/* Only show "Open in New Tab" if NOT already in standalone mode (height check is a proxy, or pass a prop) */}
      {height !== '100vh' && (
        <button
          onClick={handleOpenNewTab}
          className="absolute top-4 right-4 z-[1000] bg-white p-2 rounded-lg shadow-md hover:bg-slate-50 text-slate-600 transition-all"
          title="Open in New Tab"
        >
          <ExternalLink className="w-5 h-5" />
        </button>
      )}

      {/* SHOW LOADING */}
      {!isClient ? (
        <div className="h-full w-full bg-slate-100 flex items-center justify-center text-slate-400 animate-pulse">
          Loading map...
        </div>
      ) : !clusters ? (
        <div className="h-full w-full bg-slate-100 flex items-center justify-center text-slate-400">
          No map data available
        </div>
      ) : (
        // @ts-ignore - react-leaflet types may not match runtime props
        <MapContainer
          key={mapInstanceKey}
          center={SERVICE_AREA_CENTER}
          zoom={SERVICE_AREA_ZOOM}
          minZoom={10}
          maxBounds={chennaiBounds}
          maxBoundsViscosity={1.0}
          scrollWheelZoom={true}
          preferCanvas={true}
          style={{ height: '100%', width: '100%' }}
        >
          {/* @ts-ignore */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Map Controls Panel (right side) */}
          <MapControlsPanel
            center={SERVICE_AREA_CENTER}
            zoom={SERVICE_AREA_ZOOM}
            opacity={hexOpacity}
            onOpacityChange={onOpacityChange}
            kmzOverlay={kmzOverlay}
            onKmzUpload={onKmzUpload}
            onKmzToggle={onKmzToggle}
            onKmzRemove={onKmzRemove}
            showWeirdArea={showWeirdArea}
            onToggleWeirdArea={onToggleWeirdArea}
            onImportWeirdAreaKmz={onUpdateWeirdArea}
            isEditMode={isEditMode}
            onToggleEditMode={handleToggleEditMode}
            onExportKmz={handleExportKmz}
            weirdAreaDemandSum={weirdAreaDemandSum}
          />

          {/* Drawing controls - shown when in edit mode */}
          {isEditMode && (
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topleft"
                onCreated={handleCreated}
                onDeleted={handleDeleted}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    shapeOptions: {
                      color: '#000000',
                      fillColor: '#000000',
                      fillOpacity: 0.2,
                      weight: 3
                    }
                  }
                }}
                edit={{
                  edit: false,
                  remove: true
                }}
              />
            </FeatureGroup>
          )}

          {/* Show drawn polygons preview */}
          {drawnLayers.map((coords, idx) => (
            <Polygon
              key={`drawn-${idx}`}
              positions={coords}
              pathOptions={{
                color: '#22C55E',
                fillColor: '#22C55E',
                fillOpacity: 0.3,
                weight: 3,
                dashArray: '5, 5'
              }}
            >
              <Tooltip>
                <div className="text-xs font-semibold text-green-700">
                  New Area {idx + 1} (to be merged)
                </div>
              </Tooltip>
            </Polygon>
          ))}

          {polygons.map(poly => {
            const isSelected = selectedHexes.has(poly.hexId);
            // Calculate center of hex for label placement
            const centerLat = poly.positions.reduce((sum, p) => sum + p[0], 0) / poly.positions.length;
            const centerLng = poly.positions.reduce((sum, p) => sum + p[1], 0) / poly.positions.length;

            return (
              <React.Fragment key={poly.hexId}>
                <Polygon
                  positions={poly.positions}
                  eventHandlers={{
                    click: () => toggleSelection(poly.hexId)
                  }}
                  pathOptions={{
                    color: isSelected ? '#F59E0B' : '#000000', // Amber-500 for selection, black border otherwise
                    fillColor: poly.color,
                    fillOpacity: isSelected ? 0.9 : hexOpacity,
                    weight: isSelected ? 4 : 1,
                    stroke: true,
                    opacity: isSelected ? 1 : 0.8,
                    dashArray: isSelected ? undefined : undefined
                  }}
                />
                {/* Demand label inside hex */}
                <Marker
                  position={[centerLat, centerLng]}
                  icon={createDemandLabel(poly.demand, poly.color)}
                  interactive={false}
                />
              </React.Fragment>
            );
          })}

          {/* Service Area Overlay */}
          {showServiceArea && (
            <Polygon
              positions={serviceAreaCoordinates}
              pathOptions={{
                color: '#6366F1',
                fillColor: '#6366F1',
                fillOpacity: 0.15,
                weight: 3,
                stroke: true,
                opacity: 0.8,
                dashArray: '10, 5'
              }}
            >
              <Tooltip>
                <div className="text-xs font-semibold text-indigo-700">
                  Service Area Boundary
                </div>
              </Tooltip>
            </Polygon>
          )}

          {/* Weird Area Overlay - BLACK color with thick border */}
          {showWeirdArea && weirdAreaData.polygons.map((polygonCoords, idx) => (
            <Polygon
              key={`weird-area-${idx}`}
              positions={polygonCoords}
              pathOptions={{
                color: '#000000',
                fillColor: '#000000',
                fillOpacity: 0.1,
                weight: 5,
                stroke: true,
                opacity: 1
              }}
            >
              <Tooltip>
                <div className="text-xs font-semibold text-gray-900">
                  Weird Area Boundary {weirdAreaData.polygons.length > 1 ? `(Part ${idx + 1})` : ''}
                </div>
              </Tooltip>
            </Polygon>
          ))}

          {/* KMZ Overlay - Orange color to differentiate */}
          {kmzOverlay && kmzOverlay.visible && (
            <>
              {/* Render KMZ polygons */}
              {kmzOverlay.polygons?.map((poly, idx) => (
                <Polygon
                  key={`kmz-poly-${idx}`}
                  positions={poly.coordinates}
                  pathOptions={{
                    color: '#F97316',
                    fillColor: '#F97316',
                    fillOpacity: 0.25,
                    weight: 3,
                    stroke: true,
                    opacity: 0.9
                  }}
                >
                  <Tooltip>
                    <div className="text-xs font-semibold text-orange-700">
                      {poly.name || `KMZ Region ${idx + 1}`}
                    </div>
                  </Tooltip>
                </Polygon>
              ))}

              {/* Render KMZ points */}
              {kmzOverlay.points?.map((point, idx) => (
                <Circle
                  key={`kmz-point-${idx}`}
                  center={point.position}
                  radius={100}
                  pathOptions={{
                    color: '#EA580C',
                    fillColor: '#F97316',
                    fillOpacity: 0.8,
                    weight: 2
                  }}
                >
                  <Tooltip>
                    <div className="text-xs font-semibold text-orange-700">
                      {point.name || `Point ${idx + 1}`}
                    </div>
                  </Tooltip>
                </Circle>
              ))}
            </>
          )}
        </MapContainer>
      )}

      {/* Legend - positioned on the LEFT side */}
      <div className="absolute bottom-6 left-4 bg-white/95 backdrop-blur p-3 rounded-lg border border-slate-200 text-xs shadow-lg z-[1000]">
        <div className="font-semibold mb-2 text-slate-700">
          {metric === 'SLA' ? 'SLA Compliance' : 'Demand (Low → High)'}
        </div>

        {metric === 'SLA' ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-[hsl(120,70%,45%)]" />
              <span>High (100%)</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-[hsl(60,70%,45%)]" />
              <span>Medium (50%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[hsl(0,70%,45%)]" />
              <span>Low (0%)</span>
            </div>
          </>
        ) : (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#1E3A8A' }} />
              <span className="text-[10px]">0-10%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#2563EB' }} />
              <span className="text-[10px]">10-20%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#38BDF8' }} />
              <span className="text-[10px]">20-30%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#2DD4BF' }} />
              <span className="text-[10px]">30-40%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#86EFAC' }} />
              <span className="text-[10px]">40-50%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#FDE047' }} />
              <span className="text-[10px]">50-60%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#FB923C' }} />
              <span className="text-[10px]">60-70%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#F97316' }} />
              <span className="text-[10px]">70-80%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#EF4444' }} />
              <span className="text-[10px]">80-90%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded" style={{ backgroundColor: '#991B1B' }} />
              <span className="text-[10px]">90-100%</span>
            </div>
          </div>
        )}

        {/* KMZ Overlay Legend */}
        {kmzOverlay && kmzOverlay.visible && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded border border-orange-600" style={{ backgroundColor: '#F97316', opacity: 0.5 }} />
              <span className="text-[10px] text-orange-700 font-medium">KMZ Overlay</span>
            </div>
          </div>
        )}

        {/* Weird Area Legend */}
        {showWeirdArea && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded border-2 border-black" style={{ backgroundColor: '#000000', opacity: 0.2 }} />
              <span className="text-[10px] text-gray-900 font-medium">Weird Area</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
